/**
 * 订单转派 — 页面壳层
 *
 * 【布局自上而下】
 * 1. 标题 + 客户订单号（来自 hooks state，由路由 state / 缓存恢复）
 * 2. 转派目标供应商：`Form` + 可搜索 `Select`（仅精确匹配），错误提示见 PRD
 * 3. 转派物料明细：Table（多选 + 删除行）
 * 4. 底部固定操作栏：取消 / 确认转派
 *
 * 【字典】物料品类列使用 SYSTEM.CATEGORY，与订单详情等页一致
 */
import React from 'react'
import {
  Button,
  Form,
  Layout,
  Row,
  Col,
  Space,
  Table,
  Select,
  Typography,
  Popover,
  Input,
} from 'antd'
import { QuestionCircleOutlined } from '@ant-design/icons'
import useOrderTransferHook from './hooks'
import Style from './style.module.scss'
import { useTypedSelector } from '@/redux/store'
import { getDictLabel } from '@/modules/dict'

const { Content } = Layout
const { Title, Text } = Typography
const { Option } = Select

/** Popover：搜不到供应商时的说明（与产品文案对齐，可按运营再调）  查询不到供应商的原因：

1、请检查查询供应商是否准确输入全称，若非全称无法查询到。

2、供应商可能未与咸亨进行合作。

3、供应商可能与咸亨合作但不是当前订单所属的项目。

3、供应商合作过当前订单所属的项目，但接单权限已关闭。

若有其他疑问请联系咸亨运营人员进行处理。*/
const supplierHelpTitle = (
  <div>
    <div>查询不到供应商的原因：</div>
    <div>1、请检查查询供应商是否准确输入全称，若非全称无法查询到。</div>
    <div>2、供应商可能未与咸亨进行合作。</div>
    <div>3、供应商可能与咸亨合作但不是当前订单所属的项目。</div>
    <div>4、供应商合作过当前订单所属的项目，但接单权限已关闭。</div>
    <div>若有其他疑问请联系咸亨运营人员进行处理。</div>
  </div>
)

const OrderTransfer: React.FC = () => {
  const [form] = Form.useForm()
  const {
    state,
    supplierOptions,
    supplierSearchLoading,
    onSupplierSearch,
    onTargetSupplierChange,
    supplierFormRules,
    onMaterialRowSelectChange,
    deleteRowsDisabled,
    handleDeleteMaterialRows,
    handleCancel,
    handleConfirmTransfer,
  } = useOrderTransferHook(form)

  const dictData = useTypedSelector((s) => s.dictData.dictData)

  /** 转派物料表列定义（与示意图列顺序一致） */
  const columns = [
    {
      title: '序号',
      width: 64,
      render: (_: unknown, __: unknown, index: number) => index + 1,
    },
    {
      title: '物料编码',
      dataIndex: 'code',
      width: 120,
      ellipsis: true,
    },
    {
      title: '平台物资编码',
      dataIndex: 'platformMaterialCode',
      width: 140,
      ellipsis: true,
      render: (t: string) => t || '-',
    },
    {
      title: '物料品类',
      dataIndex: 'systemCategoryCode',
      width: 120,
      ellipsis: true,
      render: (code: string) =>
        getDictLabel('SYSTEM.CATEGORY', code || '', dictData) || '-',
    },
    {
      title: '品牌',
      dataIndex: 'brand',
      width: 100,
      ellipsis: true,
    },
    {
      title: '商品名称',
      dataIndex: 'name',
      width: 160,
      ellipsis: true,
    },
    {
      title: '型号',
      dataIndex: 'model',
      width: 100,
      ellipsis: true,
    },
    {
      title: '可转派数量',
      dataIndex: 'transferableQty',
      width: 110,
    },
    {
      title: '单位',
      dataIndex: 'unit',
      width: 72,
    },
  ]

  return (
    <Layout className={Style.layout}>
      <Content className={Style.content}>
        <div className={Style.card}>
          {/* --- 页头：标题 + 客户订单号 --- */}
          <Title level={5} className={Style.pageTitle}>
            订单转派
          </Title>
          <div className={Style.orderNoRow}>
            <Text type="secondary">客户订单号：</Text>
            <Text strong>{state.orderNo || '-'}</Text>
          </div>

          {/* --- 转派目标供应商：Form 包裹，便于展示红框与校验文案 --- */}
          <Form
            form={form}
            className={Style.supplierForm}
            layout="inline"
            colon={false}
          >
            <Row align="top" gutter={16} className={Style.supplierRow}>
              <Col flex="none">
                <Form.Item
                  label={
                    <span>
                      <span className={Style.requiredMark}>*</span>
                      转派给哪一家供应商：
                    </span>
                  }
                  name="supplierId"
                  rules={supplierFormRules}
                  validateTrigger={['onSubmit', 'onBlur']}
                  className={Style.supplierFormItem}
                >
                  <Select
                    showSearch
                    allowClear
                    placeholder="请输入"
                    className={Style.supplierSelect}
                    loading={supplierSearchLoading}
                    filterOption={false}
                    onSearch={onSupplierSearch}
                    onChange={(v) =>
                      onTargetSupplierChange(v as string | undefined)
                    }
                  >
                    {supplierOptions.map((o) => (
                      <Option key={o.value} value={o.value}>
                        {o.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item name="supplierName" noStyle>
                  <Input hidden />
                </Form.Item>
              </Col>
              <Col flex="none" className={Style.supplierHelpCol}>
                <Popover
                  content={supplierHelpTitle}
                  trigger="hover"
                  placement="topLeft"
                  overlayClassName={Style.supplierHelpPopover}
                >
                  <span className={`${Style.helpLink} primaryBtn`}>
                    <QuestionCircleOutlined /> 为什么找不到我想搜的供应商?
                  </span>
                </Popover>
              </Col>
            </Row>
          </Form>

          {/* --- 转派物料明细表 --- */}
          <div className={Style.tableSection}>
            <div className={Style.tableToolbar}>
              <Text strong>转派物料明细</Text>
              <Button
                disabled={deleteRowsDisabled}
                onClick={handleDeleteMaterialRows}
              >
                删除行
              </Button>
            </div>
            <Table
              rowKey="id"
              size="small"
              bordered
              pagination={false}
              scroll={{ x: 1100, y: 360 }}
              rowSelection={{
                selectedRowKeys: state.selectedMaterialRowKeys,
                onChange: onMaterialRowSelectChange,
              }}
              columns={columns}
              dataSource={state.materialList}
            />
          </div>
        </div>
      </Content>

      {/* --- 底部操作栏（style 使用 mixin bottom-box 贴底） --- */}
      <div className={Style.footer}>
        <Space>
          <Button onClick={handleCancel}>取消</Button>
          <Button type="primary" onClick={() => void handleConfirmTransfer()}>
            确认转派
          </Button>
        </Space>
      </div>
    </Layout>
  )
}

export default OrderTransfer
/**
 * 订单转派 — 页面壳层
 *
 * 【布局自上而下】
 * 1. 标题 + 客户订单号（来自 hooks state，由路由 state / 缓存恢复）
 * 2. 转派目标供应商：`Form` + 可搜索 `Select`（仅精确匹配），错误提示见 PRD
 * 3. 转派物料明细：Table（多选 + 删除行）
 * 4. 底部固定操作栏：取消 / 确认转派
 *
 * 【字典】物料品类列使用 SYSTEM.CATEGORY，与订单详情等页一致
 */
import React from 'react'
import {
  Button,
  Form,
  Layout,
  Row,
  Col,
  Space,
  Table,
  Select,
  Typography,
  Popover,
  Input,
} from 'antd'
import { QuestionCircleOutlined } from '@ant-design/icons'
import useOrderTransferHook from './hooks'
import Style from './style.module.scss'
import { useTypedSelector } from '@/redux/store'
import { getDictLabel } from '@/modules/dict'

const { Content } = Layout
const { Title, Text } = Typography
const { Option } = Select

/** Popover：搜不到供应商时的说明（与产品文案对齐，可按运营再调）  查询不到供应商的原因：

1、请检查查询供应商是否准确输入全称，若非全称无法查询到。

2、供应商可能未与咸亨进行合作。

3、供应商可能与咸亨合作但不是当前订单所属的项目。

3、供应商合作过当前订单所属的项目，但接单权限已关闭。

若有其他疑问请联系咸亨运营人员进行处理。*/
const supplierHelpTitle = (
  <div>
    <div>查询不到供应商的原因：</div>
    <div>1、请检查查询供应商是否准确输入全称，若非全称无法查询到。</div>
    <div>2、供应商可能未与咸亨进行合作。</div>
    <div>3、供应商可能与咸亨合作但不是当前订单所属的项目。</div>
    <div>4、供应商合作过当前订单所属的项目，但接单权限已关闭。</div>
    <div>若有其他疑问请联系咸亨运营人员进行处理。</div>
  </div>
)

const OrderTransfer: React.FC = () => {
  const [form] = Form.useForm()
  const {
    state,
    supplierOptions,
    supplierSearchLoading,
    onSupplierSearch,
    onTargetSupplierChange,
    supplierFormRules,
    onMaterialRowSelectChange,
    deleteRowsDisabled,
    handleDeleteMaterialRows,
    handleCancel,
    handleConfirmTransfer,
  } = useOrderTransferHook(form)

  const dictData = useTypedSelector((s) => s.dictData.dictData)

  /** 转派物料表列定义（与示意图列顺序一致） */
  const columns = [
    {
      title: '序号',
      width: 64,
      render: (_: unknown, __: unknown, index: number) => index + 1,
    },
    {
      title: '物料编码',
      dataIndex: 'code',
      width: 120,
      ellipsis: true,
    },
    {
      title: '平台物资编码',
      dataIndex: 'platformMaterialCode',
      width: 140,
      ellipsis: true,
      render: (t: string) => t || '-',
    },
    {
      title: '物料品类',
      dataIndex: 'systemCategoryCode',
      width: 120,
      ellipsis: true,
      render: (code: string) =>
        getDictLabel('SYSTEM.CATEGORY', code || '', dictData) || '-',
    },
    {
      title: '品牌',
      dataIndex: 'brand',
      width: 100,
      ellipsis: true,
    },
    {
      title: '商品名称',
      dataIndex: 'name',
      width: 160,
      ellipsis: true,
    },
    {
      title: '型号',
      dataIndex: 'model',
      width: 100,
      ellipsis: true,
    },
    {
      title: '可转派数量',
      dataIndex: 'transferableQty',
      width: 110,
    },
    {
      title: '单位',
      dataIndex: 'unit',
      width: 72,
    },
  ]

  return (
    <Layout className={Style.layout}>
      <Content className={Style.content}>
        <div className={Style.card}>
          {/* --- 页头：标题 + 客户订单号 --- */}
          <Title level={5} className={Style.pageTitle}>
            订单转派
          </Title>
          <div className={Style.orderNoRow}>
            <Text type="secondary">客户订单号：</Text>
            <Text strong>{state.orderNo || '-'}</Text>
          </div>

          {/* --- 转派目标供应商：Form 包裹，便于展示红框与校验文案 --- */}
          <Form
            form={form}
            className={Style.supplierForm}
            layout="inline"
            colon={false}
          >
            <Row align="top" gutter={16} className={Style.supplierRow}>
              <Col flex="none">
                <Form.Item
                  label={
                    <span>
                      <span className={Style.requiredMark}>*</span>
                      转派给哪一家供应商：
                    </span>
                  }
                  name="supplierId"
                  rules={supplierFormRules}
                  validateTrigger={['onSubmit', 'onBlur']}
                  className={Style.supplierFormItem}
                >
                  <Select
                    showSearch
                    allowClear
                    placeholder="请输入"
                    className={Style.supplierSelect}
                    loading={supplierSearchLoading}
                    filterOption={false}
                    onSearch={onSupplierSearch}
                    onChange={(v) =>
                      onTargetSupplierChange(v as string | undefined)
                    }
                  >
                    {supplierOptions.map((o) => (
                      <Option key={o.value} value={o.value}>
                        {o.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item name="supplierName" noStyle>
                  <Input hidden />
                </Form.Item>
              </Col>
              <Col flex="none" className={Style.supplierHelpCol}>
                <Popover
                  content={supplierHelpTitle}
                  trigger="hover"
                  placement="topLeft"
                  overlayClassName={Style.supplierHelpPopover}
                >
                  <span className={`${Style.helpLink} primaryBtn`}>
                    <QuestionCircleOutlined /> 为什么找不到我想搜的供应商?
                  </span>
                </Popover>
              </Col>
            </Row>
          </Form>

          {/* --- 转派物料明细表 --- */}
          <div className={Style.tableSection}>
            <div className={Style.tableToolbar}>
              <Text strong>转派物料明细</Text>
              <Button
                disabled={deleteRowsDisabled}
                onClick={handleDeleteMaterialRows}
              >
                删除行
              </Button>
            </div>
            <Table
              rowKey="id"
              size="small"
              bordered
              pagination={false}
              scroll={{ x: 1100, y: 360 }}
              rowSelection={{
                selectedRowKeys: state.selectedMaterialRowKeys,
                onChange: onMaterialRowSelectChange,
              }}
              columns={columns}
              dataSource={state.materialList}
            />
          </div>
        </div>
      </Content>

      {/* --- 底部操作栏（style 使用 mixin bottom-box 贴底） --- */}
      <div className={Style.footer}>
        <Space>
          <Button onClick={handleCancel}>取消</Button>
          <Button type="primary" onClick={() => void handleConfirmTransfer()}>
            确认转派
          </Button>
        </Space>
      </div>
    </Layout>
  )
}

export default OrderTransfer
